import React, { useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { PUBLIC_SITE } from '../../config/featureFlags';
import '../styles/fonts.css';
import '../styles/marketing.css';
import universRegular from '../../fonts/univers-next-pro/UniversNextPro-Regular.woff2';
import styreneRegular from '../../fonts/styrene-a/StyreneA-Regular.woff2';
import trustBold from '../../fonts/trust-3a/Trust3A-Bold.woff2';
import HealthCheckPanel from '../health-check/HealthCheckPanel';
import { HealthCheckProvider } from '../health-check/HealthCheckProvider';
import WaitlistPopup from '../popup/WaitlistPopup';
import Footer from './Footer';
import { HeroProvider } from './HeroContext';
import HeroStage from './HeroStage';
import { isProductionHost } from './hosts';
import Nav, { type ActivePage } from './Nav';
import { NavModeProvider, useNavMode } from './NavModeContext';
import SkipLink from './SkipLink';

export type { ActivePage };

interface MarketingLayoutProps {
  children: React.ReactNode;
  activePage?: ActivePage;
  mainClassName?: string;
  /** Focused flow (enrolment): wordmark only, no nav. */
  minimalHeader?: boolean;
  /** 0–1 completion; a thin Ink bar under the nav. */
  progress?: number | null;
  /**
   * Mount the join-the-waitlist popup. Off unless a page asks for it: this
   * layout also wraps login, the campaign and referral landers and enrolment,
   * which either carry their own capture or are mid-task.
   */
  waitlistPopup?: boolean;
}

/** The three faces above the fold; the italics and bolds load on demand. */
const PRELOAD_FONTS = [universRegular, styreneRegular, trustBold];

/** How long a route change eases the nav for; a shade over --mk-dur. */
const TRANSITION_MS = 560;

/**
 * Marks the moment after a route change so the nav eases rather than snaps
 * (NavMode.transitioning), and fades the new page's content in.
 */
function RouteTransition({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { setTransitioning } = useNavMode();
  // Layout effect: the flag must be on the nav in the same frame as the
  // scroll reset and the --hero-p change it is meant to ease.
  useLayoutEffect(() => {
    setTransitioning(true);
    const timer = setTimeout(() => setTransitioning(false), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [pathname, setTransitioning]);
  return (
    <div key={pathname} className="mk-page">
      {children}
    </div>
  );
}

/**
 * The public site's chrome — skip link, nav, the persistent hero stage,
 * main, footer, and the Health Check panel — per the brand guidelines' web
 * mockups. When rendered once by MarketingShell (a layout route) it stays
 * mounted across the hero pages, which is what lets a navigation dissolve
 * one photograph into the next and ease the nav instead of cutting. Fonts
 * and the scoped marketing stylesheet are imported here and nowhere else,
 * so the member portal never loads them. Non-production hosts are marked
 * noindex.
 */
export default function MarketingLayout({
  children,
  activePage = 'home',
  mainClassName = '',
  minimalHeader = false,
  progress = null,
  waitlistPopup = false,
}: MarketingLayoutProps) {
  const heroOnly = PUBLIC_SITE === 'hero';
  const noindex = !isProductionHost();
  const width = progress == null ? null : Math.max(0, Math.min(1, progress)) * 100;

  return (
    <NavModeProvider>
      <HeroProvider>
        <HealthCheckProvider>
          <div className="mk-root gh-surface min-h-screen flex flex-col">
            <Helmet>
              {PRELOAD_FONTS.map((href) => (
                <link key={href} rel="preload" as="font" type="font/woff2" href={href} crossOrigin="anonymous" />
              ))}
              {noindex && <meta name="robots" content="noindex, nofollow" />}
            </Helmet>

            <SkipLink />
            <Nav activePage={activePage} minimal={minimalHeader} heroOnly={heroOnly} />
            {width != null && (
              <div className="mk-progress" aria-hidden="true">
                <span className="mk-progress__fill" style={{ width: `${width}%` }} />
              </div>
            )}

            <main id="main" tabIndex={-1} className={`mk-main flex-1 ${mainClassName}`.trim()}>
              <HeroStage />
              <RouteTransition>{children}</RouteTransition>
            </main>

            <Footer heroOnly={heroOnly} />
          </div>
          <HealthCheckPanel />
          {waitlistPopup && <WaitlistPopup />}
        </HealthCheckProvider>
      </HeroProvider>
    </NavModeProvider>
  );
}
